import React, { useState } from 'react';
import { alpha, Box, ButtonBase, Chip, Collapse, Stack, Typography } from '@mui/material';
import { CheckCircleOutline, ExpandLess, ExpandMore, InfoOutlined, WarningAmberRounded } from '@mui/icons-material';
import { BoardScanTips } from './BoardScanTips';
import { PRICED_MARKETS, PricedMarket, ScanReview, SectionNotice } from '../utils/boardScanReview';

const LABEL: Record<PricedMarket, string> = { win: 'Win', top3: 'Top 3', top5: 'Top 5', top10: 'Top 10' };
const prices = (n: number) => `${n} price${n === 1 ? '' : 's'}`;
const who = (s: SectionNotice) => `${s.drivers.join(', ')}${s.n > s.drivers.length ? '…' : ''}`;

/** Market chips for a section: the one in use filled, the other likely one outlined in color. */
const MarketChips: React.FC<{ s: SectionNotice; onPick: (m: PricedMarket) => void }> = ({ s, onPick }) => (
  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
    {PRICED_MARKETS.map((m) => {
      const current = s.market === m && s.kind !== 'unclear';
      const suggested = !current && s.suggested.includes(m);
      return (
        <Chip
          key={m}
          size="small"
          label={LABEL[m]}
          onClick={() => onPick(m)}
          color={current || suggested ? 'primary' : 'default'}
          variant={current ? 'filled' : 'outlined'}
          aria-pressed={current}
        />
      );
    })}
  </Stack>
);

const sectionText = (s: SectionNotice): React.ReactNode => {
  const m = s.market ? LABEL[s.market] : '';
  const o = s.other ? LABEL[s.other] : 'another market';
  switch (s.kind) {
    case 'inferred':
      return (
        <>
          <b>No market shown</b> for {prices(s.n)} ({who(s)}): their odds match <b>{m}</b>.
        </>
      );
    case 'cautious':
      return (
        <>
          <b>
            {m} or {o}?
          </b>{' '}
          No market shown for {prices(s.n)} ({who(s)}), so they&apos;re priced as {m}, the cautious reading. If
          they&apos;re {o}, the edges are bigger.
        </>
      );
    case 'mismatch':
      return (
        <>
          <b>
            Labeled {s.labeled ? LABEL[s.labeled] : o}, but the odds look like {s.labeled === s.market ? o : m}
          </b>{' '}
          for {prices(s.n)} ({who(s)}), so they&apos;re priced as {m}, the cautious reading.
        </>
      );
    case 'unclear':
      return (
        <>
          <b>Couldn&apos;t tell the market</b> for {prices(s.n)} ({who(s)}). Pick it to price them:
        </>
      );
    case 'picked':
      return (
        <>
          Set to <b>{m}</b> for {prices(s.n)} ({who(s)}), your pick.
        </>
      );
  }
};

/** The one summary card at the top of a scan's results: what was read and priced in a line, with the details folded
 * (each section the scanner had to work out, with market chips to change it), and the screenshot tips whenever the
 * read was shaky. Opens by itself only when a section is held back for the user. */
export const BoardScanNotices: React.FC<{
  review: ScanReview;
  site: string;
  onPick: (group: string, market: PricedMarket) => void;
}> = ({ review, site, onPick }) => {
  const { sections, priced, misread, hardToRead, wrongRace, nothingRead, shaky, needsYou } = review;
  const [open, setOpen] = useState(needsYou);
  if (!shaky && sections.length === 0) return null;

  const worked = sections.filter((s) => s.kind === 'inferred').reduce((n, s) => n + s.n, 0);
  const cautious = sections.filter((s) => s.kind === 'cautious' || s.kind === 'mismatch').reduce((n, s) => n + s.n, 0);
  const held = sections.filter((s) => s.kind === 'unclear').reduce((n, s) => n + s.n, 0);
  const warn = nothingRead || !!wrongRace || !!hardToRead || held > 0;
  const tone = warn ? 'warning' : shaky ? 'info' : 'success';
  const Icon = warn ? WarningAmberRounded : shaky ? InfoOutlined : CheckCircleOutline;

  const headline = nothingRead
    ? 'No odds found in this screenshot'
    : wrongRace
      ? `This board doesn't look like ${site}`
      : `${prices(priced)} priced`;
  const parts = [
    worked > 0 && `${worked} with the market worked out from the odds`,
    cautious > 0 && `${cautious} priced cautiously`,
    held > 0 && `${held} waiting for you to pick a market`,
    misread > 0 && `${misread} couldn't be read clearly`,
  ].filter(Boolean) as string[];
  const hasDetails = sections.length > 0 || misread > 0 || !!wrongRace || nothingRead;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: (t) => alpha(t.palette[tone].main, 0.4),
        bgcolor: (t) => alpha(t.palette[tone].main, t.palette.mode === 'dark' ? 0.07 : 0.05),
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        disabled={!hasDetails}
        aria-expanded={open}
        sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.5, textAlign: 'left' }}
      >
        <Icon sx={{ fontSize: 20, color: `${tone}.main`, mt: 0.1, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {headline}
          </Typography>
          {parts.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {parts.join(' · ')}
            </Typography>
          )}
        </Box>
        {hasDetails && (
          <Typography
            variant="caption"
            sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600, flexShrink: 0 }}
          >
            {open ? 'Hide' : 'Details'}
            {open ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          </Typography>
        )}
      </ButtonBase>
      <Collapse in={open}>
        <Stack spacing={1.5} sx={{ px: 1.5, pb: 1.5, pl: 5.75, '& b': { fontWeight: 700 } }}>
          {nothingRead && (
            <Typography variant="body2">
              Is it a win, top 3, top 5, top 10 or head-to-head board for {site}? Group matchups and props aren&apos;t
              read.
            </Typography>
          )}
          {wrongRace && (
            <Typography variant="body2">
              <b>
                {wrongRace.n} of {wrongRace.of} drivers aren&apos;t in this race,
              </b>{' '}
              so their prices are left out. Is this board for {site}?
            </Typography>
          )}
          {sections.map((s) => (
            <Box key={s.group}>
              <Typography variant="body2">{sectionText(s)}</Typography>
              <MarketChips s={s} onPick={(m) => onPick(s.group, m)} />
            </Box>
          ))}
          {misread > 0 && (
            <Typography variant="body2">
              <b>{prices(misread)} couldn&apos;t be read clearly</b> (our two reads of the screenshot disagreed or only
              one saw them), so they&apos;re left out.
            </Typography>
          )}
        </Stack>
      </Collapse>
      {shaky && (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <BoardScanTips reminder />
        </Box>
      )}
    </Box>
  );
};
